import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import {
  ArrowLeft, Camera, CameraOff, CheckCircle2, XCircle, AlertTriangle, Loader2, ScanLine, KeyboardIcon, FileDown, HandCoins } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { DashboardLayout } from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";
import { api, formatDateTime, formatTime, errorMessage } from "../lib/api";
import { useLang } from "../context/LanguageContext";

export default function CheckinPage() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const [downloadingGuide, setDownloadingGuide] = useState(false);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [starting, setStarting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [result, setResult] = useState(null);
  const [manualCode, setManualCode] = useState("");
  const scannerRef = useRef(null);
  const lockRef = useRef(false);

  const refreshEvent = useCallback(() => {
    api
      .get(`/my/events/${id}`)
      .then((res) => setEvent(res.data))
      .catch(() => toast.error(t("Fant ikke arrangementet")))
      .finally(() => setLoading(false));
  }, [id, t]);

  useEffect(() => {
    refreshEvent();
  }, [refreshEvent]);

  const validate = useCallback(
    async (code) => {
      if (!code || !code.trim()) return;
      setValidating(true);
      try {
        const { data } = await api.post(`/my/events/${id}/checkin`, { code: code.trim() });
        setResult(data);
        refreshEvent();
      } catch (err) {
        setResult({ status: "invalid", message: errorMessage(err, t("Kunne ikke validere koden")) });
      } finally {
        setValidating(false);
      }
    },
    [id, refreshEvent, t]
  );

  const handleScan = useCallback(
    (decodedText) => {
      if (lockRef.current) return;
      lockRef.current = true;
      validate(decodedText);
    },
    [validate]
  );

  const startScanner = async () => {
    setStarting(true);
    try {
      const scanner = new Html5Qrcode("qr-reader");
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        handleScan,
        () => {}
      );
      setScanning(true);
    } catch (err) {
      toast.error(t("Kunne ikke starte kameraet. Gi tilgang til kamera, eller bruk manuell kode under."));
    } finally {
      setStarting(false);
    }
  };

  const stopScanner = useCallback(async () => {
    const scanner = scannerRef.current;
    if (scanner) {
      try {
        if (scanner.isScanning) {
          await scanner.stop();
        }
        scanner.clear();
      } catch {
        // ignore
      }
      scannerRef.current = null;
    }
    setScanning(false);
  }, []);

  useEffect(() => {
    return () => {
      const scanner = scannerRef.current;
      if (scanner) {
        try {
          if (scanner.isScanning) scanner.stop().catch(() => {});
        } catch {
          // ignore
        }
      }
    };
  }, []);

  const resetForNext = () => {
    setResult(null);
    setManualCode("");
    lockRef.current = false;
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (lockRef.current) return;
    lockRef.current = true;
    validate(manualCode);
  };

  const downloadInstructions = async () => {
    setDownloadingGuide(true);
    try {
      const res = await api.get(`/my/events/${id}/checkin-instructions.pdf`, { params: { lang }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = lang === "nn" ? "rightin-innsjekk-instruks-nn.pdf" : "rightin-innsjekk-instruks.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("Instruksen er lastet ned"));
    } catch (err) {
      toast.error(errorMessage(err, t("Kunne ikke lage PDF")));
    } finally {
      setDownloadingGuide(false);
    }
  };

  const statusConfig = {
    valid: {
      bg: "bg-[hsl(var(--success))]",
      text: "text-[hsl(var(--success-foreground))]",
      icon: CheckCircle2,
      title: t("Gyldig billett"),
      sub: t("Sjekket inn nå"),
    },
    // «Betal ved inngangen»: gyldig og sjekket inn, men ikke betalt – ta betalt i døren
    valid_unpaid: {
      bg: "bg-[hsl(var(--warning))]",
      text: "text-[hsl(var(--warning-foreground))]",
      icon: HandCoins,
      title: t("Gyldig – IKKE BETALT"),
      sub: t("Ta betalt ved inngangen"),
    },
    already_used: {
      bg: "bg-[hsl(var(--warning))]",
      text: "text-[hsl(var(--warning-foreground))]",
      icon: AlertTriangle,
      title: t("Allerede sjekket inn"),
      sub: t("Denne billetten er brukt"),
    },
    invalid: {
      bg: "bg-[hsl(var(--danger))]",
      text: "text-[hsl(var(--danger-foreground))]",
      icon: XCircle,
      title: t("Ugyldig kode"),
      sub: t("Fant ingen billett for dette arrangementet"),
    },
    // Refundert via Vipps: billetten finst ikkje lenger – skal avvisast i døra
    refunded: {
      bg: "bg-[hsl(var(--danger))]",
      text: "text-[hsl(var(--danger-foreground))]",
      icon: XCircle,
      title: t("Refundert – ikke gyldig"),
      sub: t("Bestillingen er refundert og billetten kan ikke brukes"),
    },
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto space-y-4">
          <Skeleton className="h-10 w-1/2" />
          <Skeleton className="h-80 rounded-2xl" />
        </div>
      </DashboardLayout>
    );
  }

  const cfg = result ? statusConfig[result.status] || statusConfig.invalid : null;
  const StatusIcon = cfg?.icon;

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors duration-150 mb-4" data-testid="checkin-back-link">
            <ArrowLeft className="w-4 h-4" />
            {t("Tilbake til oversikten")}
          </Link>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" data-testid="checkin-title">{t("Innsjekk")}</h1>
              <p className="text-muted-foreground mt-1">{event?.title} · {formatDateTime(event?.date)}</p>
            </div>
            {event && (
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" size="sm" onClick={downloadInstructions} disabled={downloadingGuide} data-testid="checkin-instructions-button">
                  {downloadingGuide ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileDown className="w-4 h-4 mr-1.5" />}
                  {t("Instruks til døren (PDF)")}
                </Button>
                <div className="rounded-xl bg-secondary px-4 py-2.5 text-sm font-medium tabular-nums" data-testid="checkin-counter">
                  {t("{a} / {b} innsjekket", { a: event.checked_in, b: event.issued })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Status panel */}
        {result && cfg && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.15 }}
          >
            <Card className={`border-0 ${cfg.bg}`} data-testid="checkin-status-panel">
              <CardContent className={`p-6 sm:p-8 ${cfg.text}`}>
                <div className="flex items-start gap-4">
                  <StatusIcon className="w-10 h-10 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h2 className="font-heading font-semibold text-2xl sm:text-3xl" data-testid="checkin-status-title">{cfg.title}</h2>
                    <p className="opacity-90 mt-0.5">{result.message ? t(result.message) : cfg.sub}</p>
                    {(result.status === "valid_unpaid" || (result.ticket?.payment === "door" && result.status === "already_used")) && (
                      <p className="mt-3 inline-flex items-center gap-2 rounded-lg bg-background/90 text-foreground px-3 py-2 text-base font-semibold" data-testid="checkin-amount-due">
                        <HandCoins className="w-5 h-5" />
                        {t("Betal ved inngangen: {n} kr", { n: result.amount_due ?? result.ticket?.unit_price ?? 0 })}
                      </p>
                    )}
                    {result.ticket && (
                      <div className="mt-4 space-y-1 text-sm">
                        <p className="font-medium text-base">
                          {result.ticket.holder_name}
                          {result.ticket.ticket_type && <span className="ml-2 font-normal opacity-90">({result.ticket.ticket_type})</span>}
                        </p>
                        <p className="opacity-90">{result.ticket.holder_email}</p>
                        <p className="font-mono opacity-90">{result.ticket.code}</p>
                        {result.status === "already_used" && result.ticket.checked_in_at && (
                          <p className="opacity-90">{t("Innsjekket kl. {time}", { time: formatTime(result.ticket.checked_in_at) })}</p>
                        )}
                      </div>
                    )}
                    {result.status === "refunded" && (
                      <div className="mt-4 space-y-1 text-sm" data-testid="checkin-refunded-details">
                        {result.holder_name && <p className="font-medium text-base">{result.holder_name}</p>}
                        <p className="font-mono opacity-90">{result.code}</p>
                        {result.refunded_at && <p className="opacity-90">{t("Refundert {time}", { time: formatDateTime(result.refunded_at) })}</p>}
                      </div>
                    )}
                  </div>
                </div>
                <Button
                  onClick={resetForNext}
                  variant="secondary"
                  className="w-full mt-6 h-12 text-base font-medium"
                  data-testid="checkin-scan-next-button"
                >
                  <ScanLine className="w-5 h-5 mr-2" />
                  {t("Skann neste")}
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {validating && (
          <div className="flex items-center justify-center gap-2 text-muted-foreground py-2" data-testid="checkin-validating">
            <Loader2 className="w-4 h-4 animate-spin" />
            {t("Validerer...")}
          </div>
        )}

        {/* Camera scanner */}
        <Card className="border-border overflow-hidden">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
                <Camera className="w-5 h-5" />
                {t("Skann QR-kode")}
              </h2>
              {scanning ? (
                <Button variant="secondary" size="sm" onClick={stopScanner} data-testid="checkin-stop-camera-button">
                  <CameraOff className="w-4 h-4 mr-1.5" />
                  {t("Stopp kamera")}
                </Button>
              ) : (
                <Button size="sm" onClick={startScanner} disabled={starting} data-testid="checkin-start-camera-button">
                  {starting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Camera className="w-4 h-4 mr-1.5" />}
                  {t("Start kamera")}
                </Button>
              )}
            </div>
            <div
              id="qr-reader"
              data-testid="checkin-camera-region"
              className={`rounded-xl bg-ink/95 ${scanning ? "h-[320px] md:h-[420px]" : "h-[180px]"} flex items-center justify-center overflow-hidden`}
            >
              {!scanning && (
                <div className="text-center text-primary-foreground/70 px-6">
                  <ScanLine className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-sm">{t("Trykk «Start kamera» for å skanne billetter, eller bruk manuell kode under.")}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Manual entry */}
        <Card className="border-border">
          <CardContent className="p-5 space-y-4">
            <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
              <KeyboardIcon className="w-5 h-5" />
              {t("Manuell kode")}
            </h2>
            <form onSubmit={handleManualSubmit} className="flex gap-2">
              <Input
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value.toUpperCase())}
                placeholder="F.eks. A1B2-C3D4"
                className="font-mono uppercase h-12 text-base tracking-wider"
                data-testid="checkin-manual-code-input"
              />
              <Button type="submit" className="h-12 px-6" disabled={validating || !manualCode.trim()} data-testid="checkin-validate-button">
                {t("Valider kode")}
              </Button>
            </form>
            <p className="text-xs text-muted-foreground">{t("Tips: Koden står under QR-koden på billetten.")}</p>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
